import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, TablePagination,
  Grid, Button, Card, CardContent
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Legend
} from "recharts";


const API_BASE = process.env.REACT_APP_API_URL;


function Issues() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showOverview, setShowOverview] = useState(false);

  const [users, setUsers] = useState([]);
  const [repos, setRepos] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const statuses = [
    { label: "Open", mappedValue: "open" },
    { label: "Closed", mappedValue: "closed" },
  ];

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setLoading(true);
        setError("");

        const params = new URLSearchParams();
        if (selectedUser) params.append("user", selectedUser);
        if (selectedRepo) params.append("repo", selectedRepo);
        if (selectedMilestone) params.append("milestone", selectedMilestone);
        if (selectedStatus) params.append("status", selectedStatus);

        const url = `${API_BASE}/api/github/issues?${params.toString()}`

        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch issues");

        const data = await response.json();
        setIssues(data);
        setError(data.length === 0 ? "No Issues Found" : "");
      } catch (error) {
        console.error("Error fetching issues:", error);
        setIssues([]);
        setError("Failed to fetch issues");
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, [selectedUser, selectedRepo, selectedMilestone, selectedStatus]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [usersRes, reposRes] = await Promise.all([
          fetch(`${API_BASE}/api/github/users`),
          fetch(`${API_BASE}/api/github/repositories`),
        ]);

        const usersData = await usersRes.json();
        const reposData = await reposRes.json();

        setUsers(Array.isArray(usersData) ? usersData.map(user => user.login) : []);
        setRepos(Array.isArray(reposData) ? reposData.map(repo => repo.name) : []);
      } catch (error) {
        console.error("Error fetching filters:", error);
        setUsers([]);
        setRepos([]);
      }
    };

    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchMilestones = async () => {
      if (!selectedRepo) {
        setMilestones([]);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/github/milestones?repo=${selectedRepo}`);
        const data = await response.json();

        if (Array.isArray(data)) {
          setMilestones(data);
        } else {
          setMilestones([]);
        }
      } catch (error) {
        console.error("Error fetching milestones:", error);
        setMilestones([]);
      }
    };

    fetchMilestones();
  }, [selectedRepo]);

  const openIssues = issues.filter(issue => issue.state === "open").length;
  const closedIssues = issues.filter(issue => issue.state === "closed").length;
  const unassignedIssues = issues.filter(issue => !issue.assignee).length;

  const pieData = [
    { name: "Open", value: openIssues },
    { name: "Closed", value: closedIssues },
  ];

  const COLORS = ["#42a5f5", "#66bb6a"];

  const repoData = Object.entries(
    issues.reduce((acc, issue) => {
      acc[issue.repository] = (acc[issue.repository] || 0) + 1;
      return acc;
    }, {})
  ).map(([repo, count]) => ({ repo, count }));

  const renderOverview = () => (
    <>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {[{
          label: "Total Issues", value: issues.length
        }, {
          label: "Open Issues", value: openIssues
        }, {
          label: "Closed Issues", value: closedIssues
        }, {
          label: "Unassigned", value: unassignedIssues
        }].map((item, index) => (
          <Grid item xs={12} md={3} key={index}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>Issue Status</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100}>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, backgroundColor: '#42a5f5', borderRadius: '50%' }} />
              <Typography variant="body2">Open</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 14, height: 14, backgroundColor: '#66bb6a', borderRadius: '50%' }} />
              <Typography variant="body2">Closed</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>Issues by Repository</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={repoData}>
              <XAxis dataKey="repo" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#1a237e" />
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box sx={{ textAlign: "center", padding: "20px" }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1a237e" }}>
        Organization Issues
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, my: 2 }}>
        <Button variant={!showOverview ? "contained" : "outlined"} onClick={() => setShowOverview(false)}>
          Issue List
        </Button>
        <Button variant={showOverview ? "contained" : "outlined"} onClick={() => setShowOverview(true)}>
          Overview
        </Button>
      </Box>

      {!showOverview && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, marginTop: "20px", flexWrap: "wrap" }}>
          <Autocomplete
            sx={{ width: 250 }}
            options={users}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => <TextField {...params} label="User" />}
          />
          <Autocomplete
            sx={{ width: 250 }}
            options={repos}
            value={selectedRepo}
            onChange={(event, newValue) => {
              setSelectedRepo(newValue);
              setSelectedMilestone(null);
            }}
            renderInput={(params) => <TextField {...params} label="Repository" />}
          />
          <Autocomplete
            sx={{ width: 250 }}
            options={milestones}
            value={selectedMilestone}
            onChange={(event, newValue) => setSelectedMilestone(newValue)}
            renderInput={(params) => <TextField {...params} label="Milestone" />}
          />
          <Autocomplete
            sx={{ width: 250 }}
            options={statuses}
            getOptionLabel={(option) => option.label}
            value={statuses.find((status) => status.mappedValue === selectedStatus) || null}
            onChange={(event, newValue) => setSelectedStatus(newValue?.mappedValue || null)}
            renderInput={(params) => <TextField {...params} label="Status" />}
          />
        </Box>
      )}

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography variant="h6" color="error" sx={{ marginTop: "20px", textAlign: "center" }}>
          {error}
        </Typography>
      ) : showOverview ? (
        renderOverview()
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: 900, margin: "auto", marginTop: 3 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#1a237e" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Issue Title</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Repository</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Assignee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((issue, index) => (
                <TableRow key={issue.id} sx={{ backgroundColor: index % 2 === 0 ? '#fef6f4' : '#f8f9fa' }}>
                  <TableCell>
                    {issue.url ? (
                      <a href={issue.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1a237e' }}>
                        {issue.title}
                      </a>
                    ) : issue.title}
                  </TableCell>
                  <TableCell>{issue.repository}</TableCell>
                  <TableCell>{issue.assignee || "Unassigned"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={issues.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
          />
        </TableContainer>
      )}
    </Box>
  );
}

export default Issues;
